/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  LabelList,
} from "recharts";
import { motion } from "framer-motion";
import { Award } from "lucide-react";

interface TopBurnerRaw {
  userAddress: string;
  NAUBurn: number;
  NAUYMint: number;
  NAUNMint: number;
}

interface TopBurner {
  user: string;
  NAUburn: number;
  NAUYmint: number;
  NAUNmint: number;
  tooltipUser?: string;
  rankLabel?: string;
}

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const { tooltipUser } = payload[0].payload || {};

  return (
    <div className="pointer-events-none">
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-gradient-to-br from-gray-400/60 to-[#021b36] text-white rounded shadow-lg p-4 border border-[#2e4b80]"
      >
        <p className="font-bold mb-2 flex items-center gap-2">
          <Award size={16} /> {tooltipUser}
        </p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-sm leading-snug">
            <span className="font-medium">{entry.name}:</span>{" "}
            <span className="font-mono">{entry.value}</span>
          </p>
        ))}
      </motion.div>
    </div>
  );
};

const renderLabel = (_entry: TopBurner, index: number) => {
  const medalColors = ["#FFD700", "#C0C0C0", "#CD7F32"];
  const color = medalColors[index] || "#032c58";

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0.7 }}
      animate={{ scale: 1, opacity: 1 }}
      className="flex items-center gap-1 text-xs font-semibold"
    >
      {index < 3 ? (
        <Award size={14} color={color} />
      ) : (
        `#${(index + 1).toString().padStart(2, "0")}`
      )}
    </motion.div>
  );
};

const BarChartSection: React.FC<{ data: TopBurnerRaw[] }> = ({ data }) => {
  const topBurners: TopBurner[] = data
    .sort((a, b) => (b.NAUBurn ?? 0) - (a.NAUBurn ?? 0))
    .map((user, i) => ({
      user: user.userAddress,
      NAUburn: user.NAUBurn ?? 0,
      NAUYmint: user.NAUYMint ?? 0,
      NAUNmint: user.NAUNMint ?? 0,
      tooltipUser: user.userAddress,
      rankLabel: `#${(i + 1).toString().padStart(2, "0")}`,
    }))
    .slice(0, 10);

  return (
    <div>
      <h2 className="text-3xl font-extrabold text-[#b30202] mb-6 text-center mt-10">
        Top Burners
      </h2>

      <div className="flex flex-col lg:flex-row items-start w-full max-w-5xl mx-auto bg-white/90 p-8 rounded shadow-xl mt-8 gap-8">
        <p className="text-gray-700 text-sm leading-relaxed">
          These community members are the most active burners of{" "}
          <strong>NAU</strong>, fueling protocol participation. Their high burn
          levels show strong conviction and involvement in shaping the
          ecosystem.
        </p>
      </div>

      <motion.div
        className="flex flex-col lg:flex-row items-start w-full max-w-5xl mx-auto bg-white/90 p-8 rounded shadow-xl mt-8 gap-8"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex-1 flex flex-col w-full">
          {!topBurners.length ? (
            <div className="flex justify-center items-center h-64">
              <p className="text-gray-500 italic">⚠️ No data available</p>
            </div>
          ) : (
            <div className="flex justify-center gap-3">
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={topBurners}
                  layout="vertical"
                  margin={{ top: 20, right: 20, bottom: 20, left: 80 }}
                  barGap={12}
                >
                  <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" />
                  <XAxis type="number" stroke="#444" tick={{ fontSize: 11 }} />
                  <YAxis
                    dataKey="rankLabel"
                    type="category"
                    tick={{ fontSize: 12, fill: "#333" }}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    verticalAlign="top"
                    wrapperStyle={{ fontSize: 13, paddingBottom: 18 }}
                  />
                  <defs>
                    <linearGradient id="gradNAU" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#f10303" stopOpacity={0.8} />
                      <stop
                        offset="50%"
                        stopColor="#ff6600"
                        stopOpacity={0.9}
                      />
                      <stop offset="100%" stopColor="#ffd700" stopOpacity={1} />
                    </linearGradient>
                  </defs>

                  <Bar
                    dataKey="NAUburn"
                    fill="url(#gradNAU)"
                    name="NAU Burned"
                    barSize={24}
                  >
                    <LabelList
                      dataKey="rankLabel"
                      content={({ index }) =>
                        typeof index === "number" &&
                        topBurners[index] &&
                        renderLabel(topBurners[index], index)
                      }
                      position="insideLeft"
                    />
                  </Bar>

                  <Bar
                    dataKey="NAUYmint"
                    fill="#f10303"
                    name="NAUY Minted"
                    barSize={16}
                  />

                  <Bar
                    dataKey="NAUNmint"
                    fill="#032c58"
                    name="NAUN Minted"
                    barSize={16}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BarChartSection;
